import React from "react";
import { AlertTriangle, CheckCircle2, History } from "lucide-react";

/**
 * What the vehicle's history says, on the case page, without being asked.
 *
 * The digital passport used to be a tab you had to think to open — which meant
 * the reviewer had to suspect pre-existing damage before they could find out
 * about it. The lookup now runs on load and reports itself, so the finding
 * arrives whether or not anyone went looking.
 *
 * Four states, and the distinction between the last two is the point:
 *
 *   pre_existing     damage on this claim was already there. The line states
 *                    the finding; a reviewer who reads nothing else can act.
 *   cleared          an earlier inspection exists and this damage is not in it.
 *                    Also worth saying — it closes the question.
 *   earlier_unknown  earlier inspections exist, not yet compared.
 *   none_earlier     history exists, but all of it postdates this claim. The
 *                    honest answer is that nothing here can establish
 *                    pre-existing damage, and it needs saying: a later
 *                    inspection read as an earlier one inverts the conclusion.
 *
 * Renders nothing when there is no other inspection. A banner announcing zero
 * records trains people to ignore the banner.
 */

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const TONES = {
  alert: {
    accent: "var(--cl-accent)",
    chipBg: "var(--cl-accent-tint)",
    chipFg: "var(--cl-accent)",
    Icon: AlertTriangle,
  },
  good: {
    accent: "var(--cl-approve)",
    chipBg: "#e3f4e8",
    chipFg: "#187a3c",
    Icon: CheckCircle2,
  },
  muted: {
    accent: "var(--cl-border-card)",
    chipBg: "var(--cl-app-bg)",
    chipFg: "var(--cl-text-muted)",
    Icon: History,
  },
};

const describe = (alert) => {
  switch (alert.kind) {
    case "pre_existing": {
      const n = alert.alreadyDamagedCount;
      return {
        tone: "alert",
        action: "Compare",
        headline: `${n} of the ${plural(alert.damagedCount, "damaged panel")} on this claim ${
          n === 1 ? "was" : "were"
        } already damaged on ${formatDate(alert.comparedAt)}`,
        detail: [
          alert.panels.join(", "),
          `${plural(alert.total, "inspection")} of this vehicle on record`,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    }
    case "cleared":
      return {
        tone: "good",
        action: "Review",
        headline: `None of this claim's damage appears in the inspection of ${formatDate(
          alert.comparedAt,
        )}`,
        detail: `${plural(alert.damagedCount, "damaged panel")} checked · ${plural(
          alert.earlierCount,
          "earlier inspection",
        )} on record`,
      };
    case "earlier_unknown":
      return {
        tone: "alert",
        action: "Compare",
        headline: `${plural(alert.earlierCount, "earlier inspection")} of this vehicle`,
        detail: `Most recent ${formatDate(
          alert.nearestEarlier?.assessedAt,
        )} · compare to see whether this damage was already there`,
      };
    default:
      return {
        tone: "muted",
        action: "View history",
        headline: `${plural(alert.total, "inspection")} of this vehicle on record, none before this case`,
        detail: "Nothing here can establish pre-existing damage",
      };
  }
};

export default function PassportAlert({ alert, onOpen, busy = false }) {
  if (!alert) return null;

  const { tone, action, headline, detail } = describe(alert);
  const { accent, chipBg, chipFg, Icon } = TONES[tone];

  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-card border border-cl-card bg-cl-surface px-[14px] py-[11px]"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <span
        className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-tile"
        style={{ background: chipBg, color: chipFg }}
      >
        <Icon className="h-[14px] w-[14px]" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-cl-text">{headline}</div>
        {detail && (
          <div className="mt-[1px] truncate text-[11.5px] text-cl-muted">
            {detail}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onOpen}
        disabled={busy}
        className={`flex-none rounded-tile px-[13px] py-[6px] text-[12px] font-semibold transition-colors ${
          tone === "alert"
            ? "bg-cl-text text-white hover:opacity-90"
            : "border border-cl-control text-cl-secondary hover:bg-cl-hover"
        } ${busy ? "cursor-wait opacity-60" : ""}`}
      >
        {busy ? "Checking…" : action}
      </button>
    </div>
  );
}
